import { type ReactElement } from 'react';
import { Badge, Text, Tooltip } from '@mantine/core';
import MegaGrid from '@/design/MegaGrid/MegaGrid';
import type { MegaGridColumn } from '@/design/MegaGrid/MegaGridTypes';
import { formatCount, formatUsd } from '@/shared/analytics/MetricFormatting';
import type { FitVerdict, LicenseFitRow } from './LicenseFitTypes';

const VERDICT_META: Record<FitVerdict, { label: string; color: string }> = {
    overpaying: { label: 'Overpaying', color: 'orange' },
    'good-value': { label: 'Good value', color: 'teal' },
    'should-license': { label: 'Should license', color: 'grape' },
    'api-optimal': { label: 'Optimal', color: 'gray' },
    'no-alternative': { label: 'No seat option', color: 'gray' },
};

function moneyCell(value: number | null): ReactElement {
    if (value === null) return <Text size="xs" c="dimmed">—</Text>;
    return <Text size="xs" ff="monospace">{formatUsd(value)}</Text>;
}

function deltaCell(row: LicenseFitRow): ReactElement {
    if (row.deltaUsd === null) return <Text size="xs" c="dimmed">—</Text>;
    const sign = row.deltaUsd > 0 ? '+' : row.deltaUsd < 0 ? '−' : '';
    return (
        <Text size="xs" ff="monospace" c={row.deltaUsd > 0 ? 'teal' : 'dimmed'}>
            {sign}
            {formatUsd(Math.abs(row.deltaUsd))}
        </Text>
    );
}

const COLUMNS: MegaGridColumn<LicenseFitRow>[] = [
    { key: 'user', header: 'User', minWidth: 180, sortValue: (r) => r.userLabel, render: (r) => <Text size="xs">{r.userLabel}</Text> },
    { key: 'provider', header: 'Provider', minWidth: 140, sortValue: (r) => r.provider, render: (r) => <Text size="xs">{r.provider}</Text> },
    {
        key: 'footing',
        header: 'Footing',
        sortValue: (r) => r.footing,
        render: (r) => (
            <Badge size="sm" variant="light" color={r.footing === 'seat' ? 'blue' : 'gray'}>
                {r.footing === 'seat' ? 'Seat' : 'API'}
            </Badge>
        ),
    },
    { key: 'actual', header: 'Actual', align: 'right', sortValue: (r) => r.actualUsd, render: (r) => moneyCell(r.actualUsd) },
    {
        key: 'alternative',
        header: 'Alternative',
        align: 'right',
        sortValue: (r) => r.alternativeUsd,
        render: (r) => (
            <Text size="xs" ff="monospace" c={r.alternativeUsd === null ? 'dimmed' : undefined}>
                {r.alternativeUsd === null ? '—' : formatUsd(r.alternativeUsd)}
                {r.lowConfidence ? ' (est.)' : ''}
            </Text>
        ),
    },
    { key: 'forecast', header: 'Forecast', align: 'right', sortValue: (r) => r.forecastUsd, render: (r) => moneyCell(r.forecastUsd) },
    { key: 'delta', header: 'Δ / mo', align: 'right', sortValue: (r) => r.deltaUsd, render: (r) => deltaCell(r) },
    {
        key: 'verdict',
        header: 'Verdict',
        sortValue: (r) => r.verdict,
        render: (r): ReactElement => {
            const badge = (
                <Badge size="sm" variant="light" color={VERDICT_META[r.verdict].color}>
                    {VERDICT_META[r.verdict].label}
                </Badge>
            );
            if (r.platformOverheadUsd <= 0) return badge;
            return (
                <Tooltip
                    multiline
                    w={264}
                    withArrow
                    label={`Actual & forecast include ${formatUsd(r.platformOverheadUsd)}/mo of platform (classifier) overhead this user drives. It’s incurred on either footing, so it doesn’t affect the verdict.`}
                >
                    {badge}
                </Tooltip>
            );
        },
    },
    { key: 'tokens', header: 'Tokens', align: 'right', sortValue: (r) => r.tokens, render: (r) => <Text size="xs" ff="monospace" c="dimmed">{formatCount(r.tokens)}</Text> },
    {
        key: 'platformOverhead',
        header: 'Platform overhead',
        align: 'right',
        defaultHidden: true,
        sortValue: (r) => r.platformOverheadUsd,
        render: (r) =>
            r.platformOverheadUsd > 0 ? (
                <Text size="xs" ff="monospace" c="grape">{formatUsd(r.platformOverheadUsd)}</Text>
            ) : (
                <Text size="xs" c="dimmed">—</Text>
            ),
    },
];

interface LicenseFitTableProps {
    rows: LicenseFitRow[];
}

/** Sortable/searchable per-user footing table; rows arrive pre-sorted by opportunity (largest Δ first). */
export default function LicenseFitTable({ rows }: LicenseFitTableProps): ReactElement {
    return (
        <MegaGrid
            persistenceKey="spend-cost-license-fit"
            columns={COLUMNS}
            rows={rows}
            getRowKey={(row) => row.key}
            searchAccessor={(row) => `${row.userLabel} ${row.userId} ${row.provider}`}
            searchPlaceholder="Filter users"
            clientPageSize={25}
            minWidth={760}
            maxHeight={620}
            emptyMessage="No seat assignments or metered usage this month."
        />
    );
}
